import { t } from "i18next";

import { DEVICE_OPTIONS } from "../../helpers/hangingProtocol/protocolEnums";
import { ReadField } from "./ReadOnly";
import "./editors.css";

/**
 * `group.devices` as a strip of toggles instead of a multi-select dropdown.
 *
 * Six options, every one of them short: behind a dropdown the whole list cost a
 * click to even read, and what had been chosen came back as a joined string.
 * Laid out flat they are one glance and one click each.
 *
 * The empty list is a real value — it means every device — so it gets a segment
 * of its own rather than reading as "nothing selected": it shows the state and
 * doubles as the way back to it.
 */
export default function DevicePicker({ value, onChange, disabled = false }) {
  const label = t("hanging-protocol.devices");
  const selected = value ?? [];
  const all = selected.length === 0;

  /* Read mode names the devices instead of showing seven buttons that cannot be
     pressed — and says "All devices" for the empty list, which is a value here
     rather than an unanswered question. */
  if (disabled) {
    const chosen = DEVICE_OPTIONS.filter((option) => selected.includes(option.value));
    return (
      <ReadField
        label={label}
        value={all ? t("hanging-protocol.devices-all") : chosen.map((o) => o.label).join(", ")}
      />
    );
  }

  const toggle = (device) =>
    onChange(
      selected.includes(device) ? selected.filter((d) => d !== device) : [...selected, device]
    );

  const segment = (key, text, pressed, onClick, extraClass = "") => (
    <button
      key={key}
      type="button"
      className={`hpv2-segment ${extraClass}`.trim()}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
    >
      {text}
    </button>
  );

  return (
    <div className="hpv2-device-picker">
      <span className="hpv2-device-picker-label">{label}</span>
      <div className="hpv2-segmented" role="group" aria-label={label}>
        {segment(
          "all",
          t("hanging-protocol.devices-all"),
          all,
          () => onChange([]),
          "hpv2-segment-all"
        )}
        {DEVICE_OPTIONS.map((option) =>
          segment(option.value, option.label, selected.includes(option.value), () =>
            toggle(option.value)
          )
        )}
      </div>
    </div>
  );
}
